import http from '@/lib/http'
import { isMpsMedRequest, isMultiProductOrderMedRequest } from '@/lib/utils'
import type { BatchMedRequest, MedRequest } from '@/types/data'
import { useQuery } from '@tanstack/react-query'
import { useFlag } from '@unleash/proxy-client-react'
import { useQuestionnaire } from './useQuestionnaire'

function hasMedRequestBeenPlacedOnHold(medRequest?: MedRequest) {
  return medRequest?.reasonForHold?.trim() || medRequest?.latestFanConversationId || medRequest?.onHoldHistory?.length
}

export default function useBatchApproval({
  medRequest,
  enabled = true
}: {
  medRequest?: MedRequest
  enabled?: boolean
}) {
  const isBatchApprovalEnabled = useFlag('batch-approval')

  const { parsedQuestions } = useQuestionnaire(medRequest?.questionaire ?? '')
  const hasFlags = parsedQuestions?.some(question => question.hasFlags)

  const query = useQuery<BatchMedRequest[]>({
    queryKey: ['batchMedicationRequestById', medRequest?.id],
    queryFn: () => http.get(`/prescriber/med-requests/${medRequest?.id}/batch`),
    enabled:
      enabled &&
      !medRequest?.patient?.scrData &&
      isBatchApprovalEnabled &&
      !!medRequest?.id &&
      !hasFlags &&
      // MPS med requests are approved/rejected via their own family fan-out, never via batch approval.
      !isMpsMedRequest(medRequest) &&
      // Likewise multi product orders: the footer's "all" actions decide them together.
      !isMultiProductOrderMedRequest(medRequest) &&
      // We should only fetch batch requests if the med request has never been on hold/FAN hold.
      !hasMedRequestBeenPlacedOnHold(medRequest)
  })

  return {
    ...query,
    shouldUseBatchApprovalLogic: !!query.data?.length && !hasFlags
  }
}
